import React, { useState, useEffect } from 'react';
import { X, Sparkles, ShoppingBag, Check, Ruler, Gift, ShieldCheck, Star } from 'lucide-react';
import { Product, NailShape, NailLength, NailSizePreset, NailSizeMeasurements } from '../types';
import { SHAPE_OPTIONS, LENGTH_OPTIONS, SIZING_PRESETS } from '../data/products';
import { NailArtPreview } from './NailArtPreview';
import { useCart } from '../context/CartContext';

interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
  onOpenSizingModal: () => void;
}

export const ProductModal: React.FC<ProductModalProps> = ({
  product,
  onClose,
  onOpenSizingModal,
}) => {
  const { addToCart } = useCart();

  const [selectedShape, setSelectedShape] = useState<NailShape>('Almond');
  const [selectedLength, setSelectedLength] = useState<NailLength>('Medium');
  const [selectedSizePreset, setSelectedSizePreset] = useState<NailSizePreset>('S');
  const [quantity, setQuantity] = useState<number>(1);
  const [justAdded, setJustAdded] = useState<boolean>(false);

  // Custom Measurements state
  const [customMeasurements, setCustomMeasurements] = useState<NailSizeMeasurements>({
    thumb: '15mm',
    index: '12mm',
    middle: '13mm',
    ring: '11mm',
    pinky: '8mm',
  });

  // Reset when product changes
  useEffect(() => {
    if (product) {
      setSelectedShape(product.defaultShape || 'Almond');
      setSelectedLength(product.defaultLength || 'Medium');
      setSelectedSizePreset('S');
      setQuantity(1);
      setJustAdded(false);
    }
  }, [product]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!product) return null;

  const handleAddToCart = () => {
    addToCart(
      product,
      selectedShape,
      selectedLength,
      selectedSizePreset,
      selectedSizePreset === 'Custom' ? customMeasurements : undefined,
      quantity
    );
    setJustAdded(true);
    setTimeout(() => {
      setJustAdded(false);
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
      <div
        className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-[#EADFCF] overflow-hidden my-auto max-h-[92vh] flex flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close Modal"
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/90 hover:bg-white text-[#78716C] hover:text-[#292524] shadow-md border border-[#E7DEC8] transition-transform hover:scale-105"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Left Column: Visual Artwork & 5-Nail Set Display */}
        <div className="w-full md:w-1/2 bg-gradient-to-b from-[#FAF5EE] via-[#FFFDF9] to-[#FAF5EE] p-6 flex flex-col justify-between border-b md:border-b-0 md:border-r border-[#EADFCF] overflow-y-auto max-h-96 md:max-h-none">
          <div className="space-y-4">
            {/* Header / Badges */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-[#854D0E] bg-[#FEF08A] px-3 py-1 rounded-full border border-[#FDE047]">
                {product.categoryLabel}
              </span>
              <div className="flex items-center gap-1 text-[#F59E0B] text-xs font-bold bg-white px-2.5 py-1 rounded-full shadow-xs border border-[#F3C053]/30">
                <Star className="w-3.5 h-3.5 fill-[#F59E0B]" />
                <span>{product.rating.toFixed(1)}</span>
                <span className="text-[#A8A29E]">({product.reviewCount} reviews)</span>
              </div>
            </div>

            {/* 5-Finger Preview Fan */}
            <div className="py-2">
              <div className="text-center mb-2">
                <span className="text-[11px] font-semibold text-[#78716C] uppercase tracking-wider">
                  Full 10-Piece Hand-Painted Set ({selectedShape} Shape)
                </span>
              </div>
              <NailArtPreview
                productId={product.id}
                shape={selectedShape}
                className="w-full h-56 sm:h-64"
                showAllFive={true}
              />
            </div>
          </div>

          {/* Included Kit Banner */}
          <div className="mt-4 p-3.5 rounded-2xl bg-white border border-[#E7DEC8] space-y-2 shadow-xs">
            <div className="flex items-center gap-2 text-xs font-bold text-[#292524]">
              <Gift className="w-4 h-4 text-[#D4AF37]" />
              <span>What’s in your $30 box:</span>
            </div>
            <ul className="text-[11px] text-[#78716C] grid grid-cols-2 gap-x-2 gap-y-1">
              <li>✓ 10 Hand-painted gel nails</li>
              <li>✓ Salon liquid nail glue</li>
              <li>✓ 24 Waterproof sticky tabs</li>
              <li>✓ Mini buffer & file</li>
              <li>✓ Wooden cuticle stick</li>
              <li>✓ 2 Alcohol prep pads</li>
            </ul>
          </div>
        </div>

        {/* Right Column: Customization Controls & Purchase */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 flex flex-col justify-between overflow-y-auto max-h-[70vh] md:max-h-[85vh] space-y-6">
          
          <div className="space-y-5">
            {/* Title & Price */}
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#292524]">
                {product.name}
              </h2>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="font-serif text-2xl font-extrabold text-[#B45309]">
                  ${product.price.toFixed(2)}
                </span>
                <span className="text-xs text-[#10B981] font-semibold bg-[#D1FAE5] px-2.5 py-0.5 rounded-full border border-[#A7F3D0]">
                  100% Hand-Painted UV Gel
                </span>
              </div>
              <p className="text-xs text-[#57534E] mt-2.5 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* 1. Select Nail Shape */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-[#292524] flex items-center justify-between">
                <span>1. Choose Nail Shape</span>
                <span className="text-[#854D0E] font-semibold">{selectedShape}</span>
              </label>
              <div className="grid grid-cols-5 gap-1.5">
                {SHAPE_OPTIONS.map((shape) => (
                  <button
                    key={shape.id}
                    type="button"
                    onClick={() => setSelectedShape(shape.id as NailShape)}
                    className={`py-2 px-1 rounded-xl text-center transition-all ${
                      selectedShape === shape.id
                        ? 'bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-white font-bold shadow-md'
                        : 'bg-[#FAF5EE] hover:bg-[#F3EBE0] text-[#57534E] text-xs font-medium border border-[#E7DEC8]'
                    }`}
                  >
                    <span className="block text-[11px]">{shape.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Select Length */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-[#292524] flex items-center justify-between">
                <span>2. Choose Length</span>
                <span className="text-[#854D0E] font-semibold">{selectedLength}</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {LENGTH_OPTIONS.map((len) => (
                  <button
                    key={len.id}
                    type="button"
                    onClick={() => setSelectedLength(len.id as NailLength)}
                    className={`py-2 px-2 rounded-xl text-center transition-all ${
                      selectedLength === len.id
                        ? 'bg-[#FEF08A] border border-[#FDE047] text-[#854D0E] font-bold shadow-xs'
                        : 'bg-[#FAF5EE] hover:bg-[#F3EBE0] text-[#57534E] text-xs font-medium border border-[#E7DEC8]'
                    }`}
                  >
                    <span className="block text-xs font-bold">{len.name}</span>
                    <span className="block text-[10px] text-[#78716C]">{len.desc.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Select Size Preset */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-[#292524]">
                  3. Choose Your Size Preset
                </label>
                <button
                  type="button"
                  onClick={onOpenSizingModal}
                  className="text-[11px] text-[#B45309] hover:underline font-semibold flex items-center gap-1"
                >
                  <Ruler className="w-3 h-3" />
                  Sizing Guide
                </button>
              </div>

              <div className="grid grid-cols-5 gap-1.5">
                {SIZING_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => setSelectedSizePreset(preset.name as NailSizePreset)}
                    className={`py-2 px-1 rounded-xl text-center transition-all ${
                      selectedSizePreset === preset.name
                        ? 'bg-[#FCE7F3] border border-[#F472B6] text-[#BE185D] font-bold shadow-xs'
                        : 'bg-[#FAF5EE] hover:bg-[#F3EBE0] text-[#57534E] text-xs font-medium border border-[#E7DEC8]'
                    }`}
                  >
                    <span className="block text-xs font-bold">{preset.name}</span>
                  </button>
                ))}
              </div>

              {/* Custom Size millimeter input if "Custom" selected */}
              {selectedSizePreset === 'Custom' && (
                <div className="mt-2 p-3 rounded-2xl bg-[#FFFDF9] border border-[#F3C053]/50 space-y-2 animate-in fade-in">
                  <span className="text-[11px] font-bold text-[#854D0E] block">
                    Enter nail width in mm (Thumb to Pinky):
                  </span>
                  <div className="grid grid-cols-5 gap-1.5">
                    {(['thumb', 'index', 'middle', 'ring', 'pinky'] as const).map((finger) => (
                      <div key={finger} className="text-center">
                        <label className="text-[10px] uppercase font-bold text-[#78716C] block capitalize">
                          {finger.slice(0, 3)}
                        </label>
                        <input
                          type="text"
                          value={customMeasurements[finger]}
                          onChange={(e) =>
                            setCustomMeasurements((prev) => ({
                              ...prev,
                              [finger]: e.target.value,
                            }))
                          }
                          className="w-full text-center py-1 px-1 text-xs font-semibold rounded-lg bg-white border border-[#E7DEC8] focus:border-[#D4AF37]"
                          placeholder="mm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-bold text-[#292524]">Quantity:</span>
              <div className="flex items-center gap-2 bg-[#FAF5EE] p-1 rounded-xl border border-[#E7DEC8]">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-7 h-7 rounded-lg bg-white text-[#292524] font-bold flex items-center justify-center hover:bg-[#F5EFE6] text-sm shadow-xs"
                >
                  -
                </button>
                <span className="w-8 text-center text-xs font-bold">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-7 h-7 rounded-lg bg-white text-[#292524] font-bold flex items-center justify-center hover:bg-[#F5EFE6] text-sm shadow-xs"
                >
                  +
                </button>
              </div>
            </div>

          </div>

          {/* Add to Cart Footer CTA */}
          <div className="pt-4 border-t border-[#F5EBE0] space-y-2">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={justAdded}
              className={`w-full py-4 px-6 rounded-2xl text-sm font-bold text-white transition-all flex items-center justify-center gap-2 shadow-lg ${
                justAdded
                  ? 'bg-[#10B981]'
                  : 'gold-shimmer-btn hover:scale-101 active:scale-98'
              }`}
            >
              {justAdded ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>Added to Cart!</span>
                </>
              ) : (
                <>
                  <ShoppingBag className="w-5 h-5" />
                  <span>Add to Bag • ${(product.price * quantity).toFixed(2)}</span>
                </>
              )}
            </button>
            <p className="text-[11px] text-center text-[#78716C]">
              Includes full prep kit • Free US shipping on orders over $50
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};
